import { useState } from 'react'
import { Icon } from '@iconify/react'
import { put } from '../../utils/Api'
import { useGlobalContext } from '../../utils/Context'
import { senderIdEnforced } from '../../utils/senderIdentity'
import {
  IntelligentCheck,
  IntelligentCheckList,
} from '../../pages/dashboard/dashPages/kyc/IntelligentCheck'
import { SenderIdPhoto } from './SenderIdPhoto'
import { senderIdDisplayUrl, uploadSenderId } from './senderIdApi'
import './SenderId.scss'

const MAX_SIZE = 3 * 1024 * 1024

type Props = {
  bookingId: string
  deliveryType?: string
  /** Private reference (or a legacy public URL) — what the booking holds. */
  senderIdImage?: string | null
  /** Signed, expiring link for displaying senderIdImage. */
  senderIdImageUrl?: string | null
  /** true / false / null — null means never checked (legacy or API booking). */
  senderIsAccountOwner?: boolean | null
  /** Intelligent check of senderIdImage (advisory). */
  senderIdCheck?: IntelligentCheck | null
  onChange: (next: {
    senderIdImage: string | null
    senderIdImageUrl: string | null
    senderIsAccountOwner: boolean | null
    senderIdCheck: IntelligentCheck | null
  }) => void
}

/**
 * View and attach the sender's government ID on an existing booking.
 *
 * The booking-time gate only catches a missing ID at the moment of creation.
 * A draft the customer abandoned, an unreadable photo, or a booking made
 * before the rule existed all need the document put on file afterwards —
 * otherwise the requirement is just an admin chasing a departed customer.
 */
function SenderIdAttach({
  bookingId,
  deliveryType,
  senderIdImage,
  senderIdImageUrl,
  senderIsAccountOwner,
  senderIdCheck,
  onChange,
}: Props) {
  const { notify } = useGlobalContext()
  const [busy, setBusy] = useState(false)

  const save = (ref: string | null) => {
    setBusy(true)
    put(`admin/booking/${bookingId}/sender-id`, { senderIdImage: ref ?? '' })
      .then((result) => {
        if (result.status === 200) {
          notify('success', result.message)
          onChange({
            senderIdImage: result.data?.senderIdImage ?? null,
            senderIdImageUrl: result.data?.senderIdImageUrl ?? null,
            senderIsAccountOwner: result.data?.senderIsAccountOwner ?? null,
            // pending after attach/replace, null after remove (or checks off)
            senderIdCheck: result.data?.senderIdCheck ?? null,
          })
        } else {
          notify('error', result.message || 'Could not save the sender ID')
        }
        setBusy(false)
      })
      .catch(() => {
        notify('error', 'Could not save the sender ID')
        setBusy(false)
      })
  }

  const handleFile = (file?: File) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      return notify('error', 'Only image files are accepted for the ID')
    }
    if (file.size > MAX_SIZE) {
      return notify('error', 'ID image size must not exceed 3MB')
    }
    setBusy(true)
    uploadSenderId(file).then((result) => {
      if (result.ok) {
        // Upload only stores the file privately — persist the reference
        // against the booking before telling the admin it is attached.
        save(result.senderIdImage)
      } else {
        notify('error', result.message)
        setBusy(false)
      }
    })
  }

  const displayUrl = senderIdDisplayUrl(senderIdImage, senderIdImageUrl)

  const inputId = `sender-id-attach-${bookingId}`

  // A result about a previous image is not this image's result.
  const currentCheck =
    senderIdCheck &&
    (!senderIdCheck.source || senderIdCheck.source === senderIdImage)
      ? senderIdCheck
      : null

  return (
    <div className='sender-id-proof'>
      <input
        type='file'
        accept='image/*'
        id={inputId}
        style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {busy ? (
        <span className='none na'>
          <Icon icon='eos-icons:loading' />
          Saving…
        </span>
      ) : senderIdImage ? (
        <>
          <SenderIdPhoto
            src={displayUrl}
            alt="Sender's government ID"
            linkClassName='thumb'
          />
          <div className='proof-actions'>
            {displayUrl && (
              <a
                className='open'
                href={displayUrl}
                target='_blank'
                rel='noreferrer'
              >
                <Icon icon='mdi:open-in-new' />
                Full size
              </a>
            )}
            <label htmlFor={inputId}>Replace</label>
            <button
              type='button'
              className='remove'
              onClick={() => save(null)}
            >
              Remove
            </button>
          </div>
          {currentCheck && (
            <div className='sender-id-check'>
              <IntelligentCheckList review={currentCheck} image='senderId' />
            </div>
          )}
        </>
      ) : (
        <>
          {senderIsAccountOwner === true ? (
            <span className='none owner'>
              <Icon icon='mdi:account-check-outline' />
              Sent by the account owner
            </span>
          ) : senderIsAccountOwner === false ? (
            <span
              className={`none ${senderIdEnforced(deliveryType) ? 'missing' : 'na'}`}
            >
              <Icon
                icon={
                  senderIdEnforced(deliveryType)
                    ? 'mdi:alert-circle-outline'
                    : 'mdi:minus-circle-outline'
                }
              />
              {senderIdEnforced(deliveryType)
                ? 'Third-party sender — no ID on file'
                : 'Third-party sender — not required on imports'}
            </span>
          ) : (
            // null: predates the rule, or came through the API channel.
            <span className='none na'>
              <Icon icon='mdi:help-circle-outline' />
              Not recorded for this booking
            </span>
          )}
          <label htmlFor={inputId} className='attach'>
            <Icon icon='mdi:paperclip' />
            Attach sender ID
          </label>
        </>
      )}
    </div>
  )
}

export default SenderIdAttach
